/**
 * Phase 2 (Feb 2026) — Open Approvals inline on Home.
 *
 * "Deine Entscheidungen" — the pending-approvals list rendered right
 * on Home, so the owner never needs to hunt for open decisions. Each
 * row shows the param_binding-hash prefix so the approval-execution
 * binding contract is visible.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import EmptyState from "./EmptyState";
import { ArrowRight } from "lucide-react";

export default function OpenDecisions() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/approvals", { params: { state: "pending", limit: 10 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <Card data-testid="open-decisions">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          Deine Entscheidungen
          <Link
            to="/app/approvals"
            className="text-xs font-normal underline text-muted-foreground hover:text-foreground"
            data-testid="open-decisions-view-all"
          >
            Alle offenen Freigaben
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items == null ? (
          <p className="text-sm text-muted-foreground">Lade offene Freigaben …</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="Keine offenen Freigaben"
            description="Sobald etwas deine Zustimmung braucht, taucht es hier auf."
            testid="open-decisions-empty"
          />
        ) : (
          <ul className="space-y-2">
            {items.map((a, i) => {
              const bp = (a?.param_binding?.hash || "").slice(0, 8);
              return (
                <li
                  key={a.id}
                  data-testid={`open-decision-${i}`}
                  className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {a.kind || a.resource_type}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>{a.resource_type}</span>
                      {bp && (
                        <>
                          <span>·</span>
                          <span className="font-mono" data-testid={`open-decision-binding-${i}`}>
                            {bp}…
                          </span>
                        </>
                      )}
                      {!bp && (
                        <Badge
                          variant="destructive"
                          className="text-[10px]"
                          data-testid={`open-decision-legacy-${i}`}
                        >
                          Legacy — keine Bindung
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" asChild>
                    <Link to={`/app/approvals`} data-testid={`open-decision-open-${i}`}>
                      Ansehen
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Link>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
